/**
 * Card compacto para o Kanban Board
 */

import React from 'react';
import { Box, Typography, IconButton, Card, CardActionArea } from '@mui/material';
import { MoreVertOutlined, LockOutlined } from '@mui/icons-material';
import { useDraggable } from '@dnd-kit/core';
import { LeadGroup } from '../../store/slices/groupsSlice';

interface KanbanCardProps {
  group: LeadGroup;
  onCardClick: (group: LeadGroup) => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, groupId: string) => void;
  isDragging?: boolean;
  isAdmin?: boolean;
  currentUserId?: string;
}

const KanbanCard: React.FC<KanbanCardProps> = ({ 
  group, 
  onCardClick, 
  onMenuOpen, 
  isDragging = false,
  isAdmin = false,
  currentUserId
}) => {
  const hasAccess = isAdmin || group.userId === currentUserId;
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: group.id,
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        backgroundColor: '#1a1a1a',
        border: '1px solid rgba(255, 107, 53, 0.15)',
        cursor: isDragging ? 'grabbing' : 'grab',
        opacity: isDragging ? 0.5 : 1,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: 'rgba(255, 107, 53, 0.4)',
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(255, 107, 53, 0.2)',
        },
      }}
      {...attributes}
      {...listeners}
    >
      <CardActionArea onClick={() => onCardClick(group)}>
        <Box sx={{ p: 1.25 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ flex: 1, pr: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#FFFFFF',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    lineHeight: 1.3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    flex: 1,
                  }}
                >
                  {group.name}
                </Typography>
                {!hasAccess && (
                  <LockOutlined sx={{ fontSize: '0.9rem', color: 'rgba(255, 107, 53, 0.7)' }} />
                )}
              </Box>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.65rem' }}>
                {new Date(group.createdAt).toLocaleDateString('pt-BR')}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onMenuOpen(e, group.id);
              }}
              sx={{ color: 'rgba(255, 255, 255, 0.5)', p: 0.25 }}
            >
              <MoreVertOutlined sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Box>

          {/* Total Leads */}
          <Box
            sx={{
              mb: 1,
              textAlign: 'center',
              py: 0.75,
              backgroundColor: 'rgba(255, 107, 53, 0.15)',
              borderRadius: 1,
            }}
          >
            <Typography variant="h5" sx={{ color: '#FF6B35', fontWeight: 700, fontSize: '1.5rem' }}>
              {group.stats?.total || 0}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.65rem' }}>
              Leads
            </Typography>
          </Box>

          {/* Mini Stats */}
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'space-between' }}>
            <Box
              sx={{
                flex: 1,
                textAlign: 'center',
                py: 0.5,
                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                borderRadius: 0.75,
              }}
            >
              <Typography variant="body2" sx={{ color: '#2196f3', fontWeight: 600, fontSize: '0.85rem' }}>
                {group.stats?.contactado || 0}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.6rem' }}>
                ✓
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                textAlign: 'center',
                py: 0.5,
                backgroundColor: 'rgba(156, 39, 176, 0.1)',
                borderRadius: 0.75,
              }}
            >
              <Typography variant="body2" sx={{ color: '#9c27b0', fontWeight: 600, fontSize: '0.85rem' }}>
                {group.stats?.emContato || 0}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.6rem' }}>
                ⟳
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                textAlign: 'center',
                py: 0.5,
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                borderRadius: 0.75,
              }}
            >
              <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 600, fontSize: '0.85rem' }}>
                {group.stats?.fechado || 0}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.6rem' }}>
                $
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                textAlign: 'center',
                py: 0.5,
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                borderRadius: 0.75,
              }}
            >
              <Typography variant="body2" sx={{ color: '#f44336', fontWeight: 600, fontSize: '0.85rem' }}>
                {group.stats?.recusou || 0}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.6rem' }}>
                ✗
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
};

export default KanbanCard;
